<template>
  <el-form
    v-if="flatDeal.stream"
    ref="formObj"
    :model="flatDeal"
    :label-position="isDesktop ? 'left' : 'top'"
    label-width="200px"
    :rules="rules"
    @submit.prevent
  >
    <el-form-item :error="errors.name" prop="name" label="Name">
      <el-input v-model="flatDeal.name" type="text" />
    </el-form-item>
    <el-form-item label="Stream">
      <StreamSelector
        v-model="flatDeal.stream"
        :disabled="disabled"
        :allowed-streams="allowedStreams"
        @update:modelValue="clearSpots"
      />
    </el-form-item>
    <el-form-item label="Ad type">
      <AdTypeSelect
        v-model="flatDeal.type_id"
        :disabled="disabled"
        :radio-view="true"
        :allowed-types="allowedTypes"
        @update:modelValue="clearSpots"
      />
    </el-form-item>
    <el-form-item
      v-if="flatDeal.type_id === AdType.Banner"
      :error="errors.size_id"
      label="Size"
    >
      <SizeSelect
        v-model="flatDeal.size_id"
        :disabled="disabled"
        :include-adaptive="isStaff"
      />
    </el-form-item>
    <ContentForm
      v-model="flatDeal"
      :loading="loading"
      @update="updateDeal"
      @subTypeChange="handleSubTypeChange"
    />
    <FlatDealDetails
      v-model="flatDeal"
      :hide-rate-fields="true"
      @update="updateDeal"
    />
    <FlatDealTargeting v-model="flatDeal" @update="updateDeal" />
    <el-form-item>
      <el-alert
        v-if="notAllowedDeal"
        type="warning"
        effect="dark"
        :closable="false"
      >
        Not enough money to create this deal
      </el-alert>
    </el-form-item>
    <el-form-item v-if="estimatingPrice" class="estimated-price-info">
      <span class="price-searching">
        <span class="price-searching__spinner" />
        Searching for deal info...
      </span>
    </el-form-item>
    <el-form-item v-else-if="estimatedPriceLabel" class="estimated-price-info">
      <span class="price-result">
        {{ estimatedPriceLabel }}
        <span v-if="expectedDealPrice" class="price-result__total">
          · Expected price: ${{ expectedDealPrice }}</span
        >
      </span>
    </el-form-item>
    <el-form-item>
      <template v-if="!priceEstimated">
        <el-button
          type="primary"
          plain
          :disabled="!canRequestPrice || estimatingPrice"
          :loading="estimatingPrice"
          @click.prevent="onRequestPrice"
        >
          Request price
        </el-button>
      </template>
      <template v-else>
        <el-button
          type="primary"
          plain
          :disabled="loading || notAllowedDeal"
          :loading="loading"
          @click.prevent="onSubmit"
        >
          Save
        </el-button>
      </template>
      <router-link to="/marketplaces" class="el-button is-plain">
        <span>Cancel</span>
      </router-link>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelector from '@/apps/traforama/components/StreamSelector.vue'
import FlatDealTargeting from './FlatDealTargeting.vue'
import FlatDealDetails from './FlatDealDetails.vue'
import ContentForm from './ContentForm.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import { FlatDealValidationErrorsKey, AdType, FlatDealType } from '@/api/types'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'
import useResize from '@/hooks/useResize'
import { estimatePrice } from '@/apps/traforama/api/flatDeals'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'NewFlatDealForm',
  components: {
    AdTypeSelect,
    StreamSelector,
    FlatDealTargeting,
    ContentForm,
    FlatDealDetails,
    SizeSelect
  },
  props: {
    modelValue: {
      required: true,
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const flatDeal = ref({ ...props.modelValue })
    const { isDesktop } = useResize()
    const formObj = ref()
    const currentSubType = ref('url')

    const { validationErrors } = useValidation(FlatDealValidationErrorsKey)
    const errors = validationErrors()

    const priceEstimated = ref(false)
    const estimatingPrice = ref(false)
    const estimatedPriceLabel = ref('')

    const formatImpressions = (n: number): string => {
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
      if (n >= 1_000) return `${Math.round(n / 1_000)}K`
      return n.toString()
    }

    const canRequestPrice = computed(() => {
      return (
        flatDeal.value.deal_type != null &&
        flatDeal.value.spot_ids?.length > 0 &&
        !!flatDeal.value.deal_start &&
        !!flatDeal.value.deal_stop
      )
    })

    const resetPrice = () => {
      priceEstimated.value = false
      estimatedPriceLabel.value = ''
      flatDeal.value.flat_rate = undefined
      flatDeal.value.daily_rate = undefined
      flatDeal.value.estimated_ecpm = undefined
      flatDeal.value.avg_daily_impressions = undefined
    }

    const onRequestPrice = async () => {
      if (!formObj.value) return

      try {
        const valid = await formObj.value.validate()
        if (!valid) return
      } catch {
        return
      }

      estimatingPrice.value = true
      try {
        const result = await estimatePrice({
          spot_id: flatDeal.value.spot_ids[0],
          type_id: flatDeal.value.type_id,
          size_id: flatDeal.value.size_id || undefined,
          country_codes: flatDeal.value.country_codes || [],
          device_ids: flatDeal.value.device_ids || [],
          deal_type: flatDeal.value.deal_type
        })

        flatDeal.value.estimated_ecpm = result.estimated_ecpm
        flatDeal.value.avg_daily_impressions = result.avg_daily_impressions

        if (
          flatDeal.value.deal_type === FlatDealType.CpmRate &&
          result.cpm_rate
        ) {
          flatDeal.value.flat_rate = result.cpm_rate
          const dailyAvg = result.avg_daily_impressions || 0
          const total = dailyAvg * totalDays.value
          const cpm = Number(result.cpm_rate).toFixed(2)
          estimatedPriceLabel.value = `CPM: $${cpm} · Daily avg: ${formatImpressions(dailyAvg)} imps · ~${formatImpressions(total)} total (${totalDays.value} days)`
        } else if (
          flatDeal.value.deal_type === FlatDealType.DailyFee &&
          result.daily_fee
        ) {
          flatDeal.value.daily_rate = result.daily_fee
          const dailyAvg = result.avg_daily_impressions || 0
          const total = dailyAvg * totalDays.value
          const fee = Number(result.daily_fee).toFixed(2)
          estimatedPriceLabel.value = `Daily fee: $${fee} · Daily avg: ${formatImpressions(dailyAvg)} imps · ~${formatImpressions(total)} total (${totalDays.value} days)`
        } else {
          flatDeal.value.estimated_ecpm = 0
          flatDeal.value.avg_daily_impressions = 0
          if (flatDeal.value.deal_type === FlatDealType.CpmRate) {
            flatDeal.value.flat_rate = 0
          } else {
            flatDeal.value.daily_rate = 0
          }
          estimatedPriceLabel.value =
            'No historical data available for price estimation'
        }

        priceEstimated.value = true
      } catch (e: any) {
        ElMessage.error('Failed to estimate price')
      } finally {
        estimatingPrice.value = false
      }
    }

    const onSubmit = () => {
      formObj.value.validate(async (valid: boolean) => {
        if (valid) {
          let subtypeId
          switch (flatDeal.value.type_id) {
            case AdType.Popunder:
              subtypeId = 10
              break
            case AdType.Banner:
              subtypeId = 3
              break
          }
          flatDeal.value.subtype_id = subtypeId
          if (flatDeal.value.deal_type === FlatDealType.CpmRate) {
            flatDeal.value.flat_rate = Number(flatDeal.value.flat_rate) || 0
          } else {
            flatDeal.value.daily_rate = Number(flatDeal.value.daily_rate) || 0
          }
          context.emit('submit', flatDeal.value)
        } else {
          return false
        }
      })
    }

    const updateDeal = async (val: any) => {
      const geoChanged =
        val.country_codes !== undefined &&
        JSON.stringify(val.country_codes) !==
          JSON.stringify(flatDeal.value.country_codes)
      const devicesChanged =
        val.device_ids !== undefined &&
        JSON.stringify(val.device_ids) !==
          JSON.stringify(flatDeal.value.device_ids)
      const spotsChanged =
        val.spot_ids !== undefined &&
        JSON.stringify(val.spot_ids) !== JSON.stringify(flatDeal.value.spot_ids)
      const dealTypeChanged =
        val.deal_type !== undefined &&
        val.deal_type !== flatDeal.value.deal_type
      const datesChanged =
        (val.deal_start !== undefined &&
          val.deal_start !== flatDeal.value.deal_start) ||
        (val.deal_stop !== undefined &&
          val.deal_stop !== flatDeal.value.deal_stop)

      const definedVal = Object.fromEntries(
        Object.entries(val).filter(([, v]) => v !== undefined)
      )
      flatDeal.value = { ...flatDeal.value, ...definedVal }

      if (
        geoChanged ||
        devicesChanged ||
        spotsChanged ||
        dealTypeChanged ||
        datesChanged
      ) {
        resetPrice()
      }
    }

    const handleSubTypeChange = (subType: string) => {
      currentSubType.value = subType
    }

    const clearSpots = () => {
      flatDeal.value.spot_ids = []
    }

    const allowedStreams = computed(() => {
      return [props.modelValue.stream]
    })

    const allowedTypes = computed(() => {
      return [props.modelValue.type_id]
    })

    const disabled = computed(() => {
      return !isStaff
    })

    const totalDays = computed(() => {
      if (!flatDeal.value.deal_start || !flatDeal.value.deal_stop) return 0

      const from = new Date(flatDeal.value.deal_start)
      const to = new Date(flatDeal.value.deal_stop)

      return (to.getTime() - from.getTime()) / (1000 * 3600 * 24) + 1
    })

    const expectedDealPrice = computed(() => {
      if (!totalDays.value) return 0

      if (flatDeal.value.deal_type === FlatDealType.CpmRate) {
        if (!flatDeal.value.flat_rate) return 0

        // daily_limit (user-defined) has priority, then avg_daily_impressions from estimation
        const dailyHits =
          flatDeal.value.daily_limit || flatDeal.value.avg_daily_impressions
        const dayPrice = dailyHits
          ? (dailyHits * flatDeal.value.flat_rate) / 1000
          : 30

        return (dayPrice * totalDays.value).toFixed(2)
      } else if (flatDeal.value.deal_type === FlatDealType.DailyFee) {
        if (!flatDeal.value.daily_rate) return 0

        return (flatDeal.value.daily_rate * totalDays.value).toFixed(2)
      }

      return 0
    })

    const notAllowedDeal = computed(() => {
      return userStore.balance / 100 < Number(expectedDealPrice.value)
    })

    const rules = computed(() => {
      const baseRules: any = {
        name: { required: true, message: 'Field is required', trigger: 'blur' },
        spot_ids: {
          type: 'array',
          required: true,
          message: 'Field is required',
          trigger: 'change'
        },
        video: {
          required: true,
          message: 'Please upload a video',
          trigger: 'blur'
        },
        icon: {
          required: true,
          message: 'Please upload an icon',
          trigger: 'blur'
        },
        // image validation will be added conditionally below
        skip_offset: {
          required: true,
          message: 'Skip offset should be 0.1 to 10',
          trigger: 'blur'
        },
        vast_tag: {
          required: true,
          message: 'Field is required',
          trigger: 'blur'
        },
        remote_url: {
          required: true,
          message: 'Field is required',
          trigger: 'blur'
        },
        deal_type: {
          required: true,
          message: 'Field is required',
          trigger: 'blur'
        }
      }

      // Validation logic based on user type and selected sub type
      if (isStaff && flatDeal.value.type_id === AdType.Banner) {
        // Validate based on currentSubType
        if (currentSubType.value === 'url') {
          // URL sub-type: link_url required, code not required
          baseRules.link_url = {
            required: true,
            message: 'Wrong url',
            trigger: 'blur'
          }
        } else if (currentSubType.value === 'code') {
          // Code sub-type: code required, link_url not required
          baseRules.code = {
            required: true,
            message: 'Field is required',
            trigger: 'blur'
          }
        }
        // Image is not required for staff Banner
      } else {
        // For non-staff users or non-Banner types: existing logic
        if (!isStaff) {
          baseRules.link_url = {
            required: true,
            message: 'Wrong url',
            trigger: 'blur'
          }
          baseRules.image = {
            required: true,
            message: 'Please upload an image',
            trigger: 'blur'
          }
          baseRules.code = {
            required: true,
            message: 'Field is required',
            trigger: 'blur'
          }
        }
      }

      return baseRules
    })

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        flatDeal.value = val
      }
    )

    return {
      onSubmit,
      onRequestPrice,
      errors,
      isDesktop,
      AdType,
      clearSpots,
      flatDeal,
      updateDeal,
      handleSubTypeChange,
      isStaff,
      notAllowedDeal,
      expectedDealPrice,
      allowedStreams,
      formObj,
      disabled,
      rules,
      allowedTypes,
      priceEstimated,
      estimatingPrice,
      estimatedPriceLabel,
      canRequestPrice
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.el-form-item) {
  margin-bottom: 10px;

  &.is-error {
    margin-bottom: 22px;
  }

  .el-form-item__label {
    padding: 0;
  }
}

:deep(.el-alert) {
  margin-bottom: 10px;
}

.estimated-price-info {
  width: 100%;
  margin-top: 8px;
}

.price-result {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 8px 14px;
  border-left: 3px solid #409eff;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
  color: #409eff;
  font-weight: 600;

  &__total {
    color: #e6a23c;
  }
}

.price-searching {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 14px;

  &__spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #c0c4cc;
    border-top-color: #409eff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.price-searching {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 14px;

  &__spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #c0c4cc;
    border-top-color: #409eff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
